/*! tools-room-render.js — window.ArtSeeRoom
 *  The room engine, lifted out of tool-see-on-wall.js so the visualiser and the wall art
 *  size calculator share one calibration instead of measuring the same rooms twice.
 *
 *  SCALE MODEL: every scene photo is 1024x1024 and carries `ppi` = pixels per inch measured
 *  AT THE WALL PLANE from one object of known size in that photo (SCENES[].ref records it).
 *  A painting of W inches is drawn W * ppi * k px wide, k = canvasCssWidth / 1024. Nothing
 *  else in this file decides a size.
 *
 *  Scene image URLs are a Liquid concern: a section publishes
 *  window.ArtSeeRoomScenes = { "1": {full, thumb}, ... }; window.SOW.scenes is the fallback.
 */
(function (w) {
  'use strict';

  var SRC = 1024; /* scene photos are 1024x1024 */

  /* ---------------------------------------------------------------- scenes
     ppi   = px per inch at the wall plane, on the 1024px source image.
     wall  = usable flat wall rectangle in source px.
     floor = y of the wall/floor junction in source px (null = out of frame).
     anchor.mode 'floor'  -> default centre 57 in above the floor (gallery standard)
                 'shelf'  -> bottom edge sits anchor.gap inches above anchor.y
     cx    = default horizontal centre of a piece, in % of the photo width. */
  var SCENES = [
    { id: 'living-room', n: 1, label: 'Living room', ppi: 7.04, wall: { x: 195, y: 120, w: 610, h: 758 }, floor: 878, anchor: { mode: 'floor' }, cx: 48.8, ref: 'floor y878 to ceiling y120 = 758 px = 108 in; cross-check 15 in planter = 105 px' },
    { id: 'sofa-wall', n: 2, label: 'Above the sofa', ppi: 7.6, wall: { x: 60, y: 58, w: 850, h: 820 }, floor: 878, anchor: { mode: 'shelf', y: 672, gap: 8 }, cx: 29.3, ref: 'floor y878 to ceiling y58 = 820 px = 108 in; cross-check sofa back 32 in = 283 px at the sofa plane' },
    { id: 'fireplace', n: 3, label: 'Fireplace alcove', ppi: 6.7, wall: { x: 320, y: 190, w: 390, h: 640 }, floor: 830, anchor: { mode: 'floor' }, cx: 50.3, ref: 'lit alcove floor y830 to soffit y190 = 640 px = 96 in' },
    { id: 'minimal-bedroom', n: 4, label: 'Above the bed', ppi: 6.6, wall: { x: 45, y: 72, w: 855, h: 728 }, floor: 800, anchor: { mode: 'shelf', y: 680, gap: 8 }, cx: 50, ref: 'queen mattress head 60 in = 395 px at the wall plane (x310-700); wall 728 px / 110 in' },
    { id: 'master-bedroom', n: 5, label: 'Headboard close-up', ppi: 8.5, wall: { x: 150, y: 145, w: 745, h: 865 }, floor: null, anchor: { mode: 'shelf', y: 718, gap: 7 }, cx: 50, ref: 'queen headboard 62 in = 525 px (x250-775) at the wall plane' },
    { id: 'dining-room', n: 6, label: 'Dining room', ppi: 5.8, wall: { x: 150, y: 150, w: 740, h: 620 }, floor: 770, anchor: { mode: 'floor' }, cx: 48.8, ref: 'floor y770 to soffit y150 = 620 px = 108 in; cross-check chair seat 18 in = 230 px' },
    { id: 'study', n: 7, label: 'Study', ppi: 6.4, wall: { x: 165, y: 115, w: 690, h: 690 }, floor: 805, anchor: { mode: 'floor' }, cx: 48.8, ref: 'floor y805 to ceiling y115 = 690 px = 108 in; cross-check desk top 30 in = 222 px' },
    { id: 'gallery-room', n: 8, label: 'Gallery room', ppi: 5.7, wall: { x: 100, y: 150, w: 810, h: 612 }, floor: 762, anchor: { mode: 'floor' }, cx: 49.3, ref: 'floor y762 to ceiling y150 = 612 px = 108 in; cross-check 72 in bench = 435 px' },
    { id: 'entryway', n: 9, label: 'Entryway', ppi: 6.5, wall: { x: 245, y: 205, w: 530, h: 620 }, floor: 825, anchor: { mode: 'floor' }, cx: 49.8, ref: 'floor y825 to soffit y205 = 620 px = 96 in; cross-check console 31 in = 247 px' },
    { id: 'stair-hall', n: 10, label: 'Stair hall', ppi: 6.6, wall: { x: 70, y: 30, w: 585, h: 865 }, floor: 897, anchor: { mode: 'floor' }, cx: 35.4, ref: 'stair riser 7 in = 46 px (eight treads, y875 to y558)' },
    { id: 'luxe-bedroom', n: 11, label: 'Panelled bedroom', ppi: 6.2, wall: { x: 265, y: 140, w: 500, h: 760 }, floor: null, anchor: { mode: 'shelf', y: 610, gap: 8 }, cx: 50, ref: 'nightstand 24 in = 145 px (x130-275); cross-check king headboard 78 in = 475 px' }
  ];

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function sceneById(id) { for (var i = 0; i < SCENES.length; i++) if (SCENES[i].id === id) return SCENES[i]; return null; }
  function wallWidthIn(sc) { return sc.wall.w / sc.ppi; }

  function sceneUrl(n, thumb) {
    var m = w.ArtSeeRoomScenes || (w.SOW && w.SOW.scenes) || null;
    var u = m && m[n];
    return u ? (thumb ? u.thumb : u.full) : '';
  }

  /* one in-memory cache for both tools: a scene photo is fetched once per page */
  var imgs = {};
  function loadImage(src, anon) {
    if (!src) return Promise.reject(new Error('ArtSeeRoom: empty image src'));
    var key = (anon === false ? 'l|' : 'a|') + src;
    if (imgs[key]) return imgs[key];
    var p = new Promise(function (res, rej) {
      var i = new Image();
      if (anon !== false) i.crossOrigin = 'anonymous';
      i.onload = function () { res(i); };
      i.onerror = function () { rej(new Error('ArtSeeRoom: image failed ' + src)); };
      i.src = src;
    });
    p.catch(function () { delete imgs[key]; });
    imgs[key] = p;
    return p;
  }

  /* a frame is 7% of the piece, held between the 1.6 and 3.2 in mouldings we sell */
  function frameWidthIn(wIn, frame) { return (!frame || frame === 'none') ? 0 : clamp(wIn * 0.07, 1.6, 3.2); }

  function paintFrame(c, frame, x, y, w2, h, fw, depth) {
    var g;
    if (frame === 'black') { g = c.createLinearGradient(x, y, x, y + h); g.addColorStop(0, '#2c2c2c'); g.addColorStop(.5, '#141414'); g.addColorStop(1, '#232323'); }
    else if (frame === 'walnut') { g = c.createLinearGradient(x, y, x + w2, y + h); g.addColorStop(0, '#7a5130'); g.addColorStop(.45, '#5a3a21'); g.addColorStop(1, '#3d2715'); }
    else { g = c.createLinearGradient(x, y, x + w2, y + h); g.addColorStop(0, '#e6c766'); g.addColorStop(.4, '#c2a03c'); g.addColorStop(1, '#8c6d1f'); }
    c.fillStyle = g; c.fillRect(x, y, w2, h);
    /* bevel: light from top-left, so the inner top/left edge catches light */
    c.fillStyle = 'rgba(255,255,255,.18)'; c.fillRect(x, y, w2, Math.max(1, fw * .22)); c.fillRect(x, y, Math.max(1, fw * .22), h);
    c.fillStyle = 'rgba(0,0,0,.28)'; c.fillRect(x, y + h - Math.max(1, fw * .22), w2, Math.max(1, fw * .22)); c.fillRect(x + w2 - Math.max(1, fw * .22), y, Math.max(1, fw * .22), h);
    if (depth > 0) { c.fillStyle = 'rgba(0,0,0,.30)'; c.fillRect(x + w2, y + depth, depth, h); c.fillRect(x + depth, y + h, w2, depth); }
  }

  /* One piece, centred on (cx, cy) in canvas CSS px. The caller turns inches into pixels;
     this owns shadow, frame and depth, which is what separates a photo from a sticker. */
  function drawArt(c, o) {
    if (!o.img) return null;
    var w2 = o.wPx, h = o.hPx, fw = o.framePx || 0, depth = o.depth == null ? 3 : o.depth;
    var x = o.cx - w2 / 2, y = o.cy - h / 2;
    var ox = x - fw, oy = y - fw, ow = w2 + fw * 2, oh = h + fw * 2;
    c.save();
    c.shadowColor = 'rgba(0,0,0,.25)';
    c.shadowBlur = o.blur == null ? 14 : o.blur;
    c.shadowOffsetX = o.sx == null ? 4 : o.sx;
    c.shadowOffsetY = o.sy == null ? 8 : o.sy;
    c.fillStyle = '#000'; c.fillRect(ox, oy, ow, oh);
    c.restore();
    if (fw > 0) paintFrame(c, o.frame, ox, oy, ow, oh, fw, depth);
    try { c.drawImage(o.img, x, y, w2, h); } catch (e) { }
    if (fw === 0) { /* gallery-wrap depth on a bare canvas */
      c.fillStyle = 'rgba(0,0,0,.22)'; c.fillRect(x + w2, y + depth, depth, h); c.fillRect(x + depth, y + h, w2, depth);
    }
    c.strokeStyle = 'rgba(0,0,0,.16)'; c.lineWidth = 1; c.strokeRect(ox + .5, oy + .5, ow - 1, oh - 1);
    return { x: ox, y: oy, w: ow, h: oh, ix: x, iy: y, iw: w2, ih: h };
  }

  /* --------------------------------------------------------------- render */
  /* render(canvas, {scene, artUrl, widthIn, heightIn, frame, centerIn, gapIn, minGapIn,
                     width, cxPct})
     -> Promise<{scene, ppi, k, cssW, widthPx, heightPx, box}>
     Vertical placement, in order of authority:
       1. centerIn on a scene whose floor line is known  -> the published number wins;
       2. the scene's own shelf anchor + gapIn          -> when the floor is out of frame;
       3. 42% of the frame                              -> nothing else is known.
     Either way the piece is then pushed inside the usable wall rectangle and never
     allowed closer than minGapIn to the furniture in the photograph. */
  function render(canvas, o) {
    o = o || {};
    var sc = typeof o.scene === 'string' ? sceneById(o.scene) : o.scene;
    if (!sc) sc = SCENES[0];
    var jobs = [loadImage(sceneUrl(sc.n, false))];
    jobs.push(o.artUrl ? loadImage(o.artUrl).catch(function () { return null; }) : Promise.resolve(null));
    return Promise.all(jobs).then(function (r) {
      var bg = r[0], art = r[1];
      var cssW = Math.round(o.width || canvas.clientWidth || (canvas.parentNode && canvas.parentNode.clientWidth) || 560);
      var k = cssW / SRC;
      /* The photos are square, but a square canvas puts the furniture below the fold, and
         seeing the piece against the sofa is the whole point. So trim ceiling, never floor:
         keep everything from just above the wall rect down past the floor line. `trim` is in
         source px and the whole context shifts by it, so every y computed from the scene
         (anchor, floor, wall) still lands where it belongs. */
      var trim = 0;
      if (o.fit === true) {   /* opt-in: Tool 3 draws its own overlay in canvas space and keeps the square frame */
        /* How high the piece will actually sit, in source px, so the trim never eats it.
           Mirrors the cy maths below; the guard is the top edge of the frame, not the top
           of the wall rect, because a wall that starts at y=58 would otherwise pin the
           crop to 46 px and leave the sofa off screen. */
        var hSrc = (o.heightIn || 0) * sc.ppi;
        var cySrc = (sc.floor != null && o.centerIn) ? (sc.floor - o.centerIn * sc.ppi)
          : (sc.anchor.mode === 'shelf' ? sc.anchor.y - (o.gapIn != null ? o.gapIn : sc.anchor.gap) * sc.ppi - hSrc / 2
            : SRC * 0.42);
        var artTop = cySrc - hSrc / 2 - 44;           /* 44 px of air above the frame */
        var bottom = Math.min(SRC, (sc.floor || SRC) + 40);
        var want = Math.round(SRC * 0.74);
        trim = Math.max(0, Math.min(bottom - want, Math.max(0, Math.round(artTop))));
      }
      var srcH = SRC - trim;
      var cssH = Math.round(srcH * k);
      var dpr = Math.min(w.devicePixelRatio || 1, 2);
      canvas.width = Math.round(cssW * dpr); canvas.height = Math.round(cssH * dpr);
      canvas.style.width = cssW + 'px'; canvas.style.height = cssH + 'px';
      var c = canvas.getContext('2d');
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.clearRect(0, 0, cssW, cssH);
      c.translate(0, -trim * k);
      try { c.drawImage(bg, 0, 0, cssW, SRC * k); } catch (e) { }
      var out = { scene: sc, ppi: sc.ppi, k: k, cssW: cssW, widthPx: 0, heightPx: 0, box: null };
      if (!art || !o.widthIn) return out;

      var wPx = o.widthIn * sc.ppi * k, hPx = o.heightIn * sc.ppi * k;
      var fwPx = frameWidthIn(o.widthIn, o.frame) * sc.ppi * k;
      var cy;
      if (sc.floor != null && o.centerIn) cy = (sc.floor - o.centerIn * sc.ppi) * k;
      else if (sc.anchor.mode === 'shelf') cy = (sc.anchor.y - (o.gapIn != null ? o.gapIn : sc.anchor.gap) * sc.ppi) * k - hPx / 2;
      else cy = SRC * k * 0.42; /* source space: the context is already translated by `trim` */
      if (sc.anchor.mode === 'shelf') {
        var floorBottom = (sc.anchor.y - (o.minGapIn == null ? 5 : o.minGapIn) * sc.ppi) * k;
        if (cy + hPx / 2 > floorBottom) cy = floorBottom - hPx / 2;
      }
      var top = sc.wall.y * k + 2, bot = (sc.wall.y + sc.wall.h) * k - 2;
      var loY = top + hPx / 2 + fwPx, hiY = bot - hPx / 2 - fwPx;
      if (loY < hiY) cy = clamp(cy, loY, hiY);

      var cx = (o.cxPct != null ? o.cxPct : sc.cx) / 100 * cssW;
      var loX = sc.wall.x * k + wPx / 2 + fwPx, hiX = (sc.wall.x + sc.wall.w) * k - wPx / 2 - fwPx;
      if (loX < hiX) cx = clamp(cx, loX, hiX);

      out.widthPx = wPx; out.heightPx = hPx;
      out.box = drawArt(c, {
        img: art, cx: cx, cy: cy, wPx: wPx, hPx: hPx,
        frame: o.frame, framePx: fwPx,
        depth: clamp(2 * k * (cssW / 900), 1, 4),
        blur: clamp(cssW / 62, 4, 18), sx: clamp(cssW / 260, 1, 6), sy: clamp(cssW / 130, 2, 12)
      });
      return out;
    });
  }

  w.ArtSeeRoom = {
    SRC: SRC,
    scenes: SCENES,
    sceneById: sceneById,
    sceneUrl: sceneUrl,
    wallWidthIn: wallWidthIn,
    loadImage: loadImage,
    frameWidthIn: frameWidthIn,
    paintFrame: paintFrame,
    drawArt: drawArt,
    render: render
  };
})(window);
